import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import Button from '../components/ui/Button';
import XenditPaymentMethods from '../components/ui/XenditPaymentMethods';
import { useCheckout } from '../context/CheckoutContext';
import { getRegionType } from '../utils/deliveryUtils';
import { formatIdrFromUsd, getCachedUsdToIdrRate, ensureUsdToIdrRate } from '../utils/currency';

const Payment = () => {
  const navigate = useNavigate();
  const { checkoutData, hasRequiredDataForStep } = useCheckout();
  const { 
    productId, 
    productName, 
    selectedSize, 
    price, 
    shippingAddress, 
    shipping
  } = checkoutData;

  // Check if we have the required data for this step
  useEffect(() => {
    if (!hasRequiredDataForStep('payment')) {
      navigate('/checkout/shipping-options');
    }
  }, [hasRequiredDataForStep, navigate]);
  
  const [usdToIdrRate, setUsdToIdrRate] = useState(getCachedUsdToIdrRate());
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  const [errors, setErrors] = useState({});
  const [processing, setProcessing] = useState(false);
  
  useEffect(() => {
    let isMounted = true;
    ensureUsdToIdrRate().then(rate => {
      if (isMounted) {
        setUsdToIdrRate(rate);
      }
    });
    return () => {
      isMounted = false;
    };
  }, []);

  // Determine region type based on shipping address
  const regionType = shippingAddress ? getRegionType(shippingAddress) : 'unknown';
  const isBali = regionType === 'bali';
  
  // Handle payment method selection
  const handlePaymentMethodChange = (method) => {
    console.log('🔵 PAYMENT METHOD SELECTED:', method);
    setSelectedPaymentMethod(method);
    setErrors({});
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};
    
    if (!selectedPaymentMethod) {
      newErrors.paymentMethod = 'Please select a payment method';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('🚀 PAYMENT FORM SUBMITTED');
    console.log('🔵 Form validation result:', validateForm());
    
    if (validateForm()) {
      try {
        setProcessing(true);
        console.log('🔄 Processing payment...');

        // Client-side guard: OTC store limits (e.g., Indomaret/Alfamart max Rp 5,000,000)
        if (selectedPaymentMethod?.type === 'OVER_THE_COUNTER') {
          const totalUsd = calculateTotal();
          const totalIdr = Math.round(totalUsd * usdToIdrRate);
          if ((selectedPaymentMethod.channelCode === 'INDOMARET' || selectedPaymentMethod.channelCode === 'ALFAMART') && totalIdr > 5000000) {
            const msg = 'For Indomaret/Alfamart the maximum per payment is Rp 5.000.000. Please choose Bank Transfer (Virtual Account) or split your order.';
            setErrors({ form: msg });
            setProcessing(false);
            return;
          }
        }
        
        // Handle different payment methods
        if (selectedPaymentMethod.type === 'CASH_IN_STORE') {
          // Cash payment workflow - create order and redirect to instructions
          await handleCashPayment();
          return;
        } else if (selectedPaymentMethod.type === 'TEST_CARD') {
          // Test card payment workflow
          await handleTestCardPayment();
          return;
        }

        // Legacy workflow for other payment methods
        const orderData = {
          productId,
          productName,
          selectedSize,
          price: price,
          shippingAddress,
          shipping,
          total: calculateTotal(),
          paymentMethod: `${selectedPaymentMethod.type}${selectedPaymentMethod.channelCode ? `_${selectedPaymentMethod.channelCode}` : ''}`,
          status: 'PENDING'
        };

        console.log('📝 Creating order with data:', JSON.stringify(orderData, null, 2));

        // Create order in database first
        const API_BASE_URL = process.env.NODE_ENV === 'production' 
          ? '/api' 
          : (process.env.REACT_APP_API_URL || '/api');
        
        const orderUrl = `${API_BASE_URL}/orders`;
        console.log('🌐 Order creation URL:', orderUrl);
        console.log('🌐 Environment:', process.env.NODE_ENV);
        console.log('🌐 API Base URL:', API_BASE_URL);

        const createOrderResponse = await fetch(orderUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(orderData),
        });

        console.log('📊 Order creation response status:', createOrderResponse.status);
        console.log('📊 Order creation response headers:', Object.fromEntries(createOrderResponse.headers.entries()));

        if (!createOrderResponse.ok) {
          const errorText = await createOrderResponse.text();
          console.error('❌ Order creation failed:', createOrderResponse.status, errorText);
          throw new Error(`Failed to create order: ${createOrderResponse.status} - ${errorText}`);
        }

        const createdOrder = await createOrderResponse.json();
        console.log('✅ Order created successfully:', createdOrder);
        const orderId = createdOrder.id;

        // Now create Xendit payment request with correct data structure
        const xenditPayload = {
          orderId: orderId,
          amount: calculateTotal(),
          paymentMethodType: selectedPaymentMethod.type,
          channelCode: selectedPaymentMethod.channelCode,
          customerData: {
            email: shippingAddress.email || 'customer@kalahats.com',
            phone: shippingAddress.phone || '+62812345678',
            firstName: shippingAddress.fullName.split(' ')[0],
            lastName: shippingAddress.fullName.split(' ').slice(1).join(' ') || '',
            address: {
              country: shippingAddress.country || 'ID',
              street: shippingAddress.addressLine1 || '',
              city: shippingAddress.city || '',
              state: shippingAddress.state || '',
              postalCode: shippingAddress.postalCode || ''
            }
          },
          description: `KALA HATS Order #${orderId}`
        };
        
        console.log('💳 Creating Xendit payment request with payload:', JSON.stringify(xenditPayload, null, 2));
        
        // Create Xendit payment request using same API base URL
        const xenditUrl = `${API_BASE_URL}/xendit/payment-request`;
        console.log('🌐 Xendit API URL:', xenditUrl);
          
        const response = await fetch(xenditUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(xenditPayload),
        });
        
        console.log('📊 Xendit API response status:', response.status);
        console.log('📊 Xendit API response headers:', Object.fromEntries(response.headers.entries()));
        
        if (!response.ok) {
          const errorData = await response.json().catch(() => null);
          const errorText = await response.text().catch(() => 'Unknown error');
          console.error('❌ Xendit payment request failed:', {
            status: response.status,
            statusText: response.statusText,
            errorData,
            errorText
          });
          const msg = errorData?.error || errorData?.message || errorText || `Failed to create payment request: ${response.status}`;
          throw new Error(msg);
        }
        
        const paymentResponse = await response.json();
        console.log('✅ Xendit payment request created successfully:', JSON.stringify(paymentResponse, null, 2));
        
        // Handle different payment flows based on payment method
        console.log('🔄 Processing payment response...');
        console.log('🔍 Payment actions:', paymentResponse.actions);
        
        if (paymentResponse.actions && paymentResponse.actions.length > 0) {
          const redirectAction = paymentResponse.actions.find(a => a.action === 'REDIRECT' || a.action === 'AUTH');
          const qrAction = paymentResponse.actions.find(a => a.action === 'QR_CHECKOUT');
          
          if (redirectAction?.url) {
            console.log('🔄 Redirecting to payment page:', redirectAction.url);
            window.location.href = redirectAction.url;
          } else if (qrAction?.qr_code || qrAction?.qrCode) {
            const qr = qrAction.qr_code || qrAction.qrCode;
            console.log('📱 Navigating to QR checkout page');
            navigate('/checkout/qr', {
              state: {
                qrCode: qr,
                orderId: paymentResponse.orderId,
                paymentId: paymentResponse.xenditPaymentId
              }
            });
          } else {
            console.log('ℹ️ No redirect/QR actions. Showing instructions.');
            navigate('/checkout/payment-instructions', {
              state: {
                paymentData: paymentResponse,
                orderId: paymentResponse.orderId,
                paymentMethod: selectedPaymentMethod
              }
            });
          }
        } else {
          console.log('📄 Navigating to payment instructions page (no redirect actions)');
          // For methods that don't require redirect (like virtual accounts)
          navigate('/checkout/payment-instructions', {
            state: {
              paymentData: paymentResponse,
              orderId: paymentResponse.orderId,
              paymentMethod: selectedPaymentMethod
            }
          });
        }
        
      } catch (error) {
        console.error('❌ PAYMENT PROCESSING ERROR:', error);
        console.error('❌ Error stack:', error.stack);
        console.error('❌ Error name:', error.name);
        console.error('❌ Error message:', error.message);
        
        setErrors({
          form: error.message || 'Payment processing failed. Please try again.'
        });
        setProcessing(false);
      }
    } else {
      console.log('❌ Form validation failed');
    }
  };
  
  // Handle cash payment workflow
  const handleCashPayment = async () => {
    console.log('💰 Processing cash payment...');
    
    const API_BASE_URL = process.env.NODE_ENV === 'production' 
      ? '/api' 
      : (process.env.REACT_APP_API_URL || '/api');

    const orderData = {
      productId,
      productName,
      selectedSize,
      price: price,
      shippingAddress,
      shipping,
      deliveryMethod: checkoutData.deliveryMethod,
      deliveryCost: checkoutData.deliveryCost,
      total: calculateTotal(),
      paymentMethod: 'CASH_IN_STORE',
      status: 'PENDING',
      paymentStatus: 'AWAITING_PREPAYMENT'
    };

    const response = await fetch(`${API_BASE_URL}/orders`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(orderData),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to create order: ${response.status} - ${errorText}`);
    }

    const createdOrder = await response.json();
    console.log('✅ Cash payment order created:', createdOrder);

    // Navigate to cash payment instructions
    navigate('/checkout/success', {
      state: {
        orderNumber: createdOrder.orderNumber,
        paymentMethod: 'CASH_IN_STORE',
        cashPaymentInstructions: true,
        total: calculateTotal()
      }
    });
  };

  // Handle test card payment workflow
  const handleTestCardPayment = async () => {
    console.log('💳 Processing test card payment...');
    
    const API_BASE_URL = process.env.NODE_ENV === 'production' 
      ? '/api' 
      : (process.env.REACT_APP_API_URL || '/api');

    const orderData = {
      productId,
      productName,
      selectedSize,
      price: price,
      shippingAddress,
      shipping,
      deliveryMethod: checkoutData.deliveryMethod,
      deliveryCost: checkoutData.deliveryCost,
      total: calculateTotal(),
      paymentMethod: 'TEST_CARD',
      status: 'PROCESSING',
      paymentStatus: 'SUCCEEDED' // Auto-mark as paid for testing
    };

    const response = await fetch(`${API_BASE_URL}/orders`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(orderData),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to create order: ${response.status} - ${errorText}`);
    }

    const createdOrder = await response.json();
    console.log('✅ Test card payment order created:', createdOrder);

    // Simulate a brief processing delay
    setTimeout(() => {
      navigate('/checkout/success', {
        state: {
          orderNumber: createdOrder.orderNumber,
          paymentMethod: 'TEST_CARD',
          testPayment: true,
          total: calculateTotal()
        }
      });
    }, 1500);
  };

  // Calculate total price
  const calculateTotal = () => {
    const productPrice = typeof price === 'number' ? price : Number(price) || 0;
    const shippingCost = (shipping && typeof shipping.cost === 'number') ? shipping.cost : 0;
    return productPrice + shippingCost;
  };
  
  // Check if we have the necessary data
  if (!productId || !price || !shippingAddress || !shipping) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-16 text-center">
          <h1 className="font-serif text-3xl font-bold text-primary mb-6">Missing Order Information</h1>
          <p className="text-light mb-8">Please start your order from the product page.</p>
          <Button onClick={() => navigate('/products')} variant="primary">
            Browse Products
          </Button>
        </div>
      </Layout>
    );
  }
  
  return (
    <Layout>
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-3xl mx-auto">
          {/* Checkout Steps */}
          <div className="mb-8">
            <div className="flex justify-between items-center">
              <div className="flex flex-col items-center">
                <div className="w-10 h-10 bg-gray-700 text-gray-400 rounded-full flex items-center justify-center font-bold">1</div>
                <span className="text-gray-400 text-sm mt-2">Confirmation</span>
              </div>
              <div className="flex-1 h-1 bg-gray-700 mx-2"></div>
              <div className="flex flex-col items-center">
                <div className="w-10 h-10 bg-gray-700 text-gray-400 rounded-full flex items-center justify-center font-bold">2</div>
                <span className="text-gray-400 text-sm mt-2">Shipping</span>
              </div>
              <div className="flex-1 h-1 bg-gray-700 mx-2"></div>
              <div className="flex flex-col items-center">
                <div className="w-10 h-10 bg-primary text-dark rounded-full flex items-center justify-center font-bold">3</div>
                <span className="text-primary text-sm mt-2">Payment</span>
              </div>
            </div>
          </div>
          
          <h1 className="font-serif text-3xl font-bold text-primary mb-6">
            Payment
          </h1>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <div className="bg-secondary border border-gray-800 rounded-lg overflow-hidden mb-8">
                <div className="p-6">
                  <h2 className="font-serif text-xl font-bold text-primary mb-6">
                    Select Payment Method
                  </h2>
                  
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {errors.form && (
                      <div className="bg-red-900/20 text-red-400 p-4 rounded-md mb-6">
                        <p>{errors.form}</p>
                      </div>
                    )}
                    
                    {/* Payment Method Selection */}
                    <div className="space-y-4">
                      {errors.paymentMethod && (
                        <p className="text-red-500 text-sm mb-2">{errors.paymentMethod}</p>
                      )}
                      
                      <XenditPaymentMethods
                        onPaymentMethodSelect={handlePaymentMethodChange}
                        selectedMethod={selectedPaymentMethod}
                        totalUsd={calculateTotal()}
                      />
                      
                      {/* Region info */}
                      <div className="mt-4 p-4 bg-gray-800/50 rounded-md">
                        <p className="text-sm text-gray-400">
                          {isBali ? (
                            <>
                              🏝️ <strong>Bali Address Detected:</strong> Card, QRIS, virtual account, e-wallet, and OTC payment channels are available based on Xendit activation.
                            </>
                          ) : (
                            <>
                              🌍 <strong>International/Domestic Address:</strong> Available payment channels are shown dynamically from Xendit.
                            </>
                          )}
                        </p>
                      </div>
                    </div>
                    
                    
                    {/* Payment Information */}
                    {selectedPaymentMethod && (
                      <div className="bg-dark border border-gray-700 rounded-md p-4">
                        <h3 className="font-bold text-light mb-2">Selected Payment Method</h3>
                        <div className="flex items-center">
                          <span className="text-gray-400 mr-2">Method:</span>
                          <span className="text-light font-medium">
                            {selectedPaymentMethod.name}
                            {selectedPaymentMethod.country && (
                              <span className="ml-2 text-xs text-gray-400">({selectedPaymentMethod.country})</span>
                            )}
                          </span>
                        </div>
                        {selectedPaymentMethod.description && (
                          <p className="text-gray-400 text-sm mt-2">{selectedPaymentMethod.description}</p>
                        )}
                      </div>
                    )}
                    
                    <div className="pt-4 flex justify-between">
                      <Button 
                        type="button" 
                        variant="secondary" 
                        onClick={() => navigate(-1)}
                        disabled={processing}
                      >
                        Back
                      </Button>
                      <Button 
                        type="submit" 
                        variant="primary"
                        disabled={processing || !selectedPaymentMethod}
                      >
                        {processing ? (
                          <span className="flex items-center">
                            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-dark" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Creating Payment...
                          </span>
                        ) : (
                          `Pay ${formatIdrFromUsd(calculateTotal())}`
                        )}
                      </Button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
            
            <div className="lg:col-span-1">
              <div className="bg-secondary border border-gray-800 rounded-lg overflow-hidden sticky top-8">
                <div className="p-6">
                  <h2 className="font-serif text-xl font-bold text-primary mb-4">
                    Order Summary
                  </h2>
                  <div className="space-y-2 mb-4">
                    <div className="flex justify-between">
                      <span className="text-gray-400">Product</span>
                      <span className="text-light">{productName}</span>
                    </div>
                    {selectedSize && (
                      <div className="flex justify-between">
                        <span className="text-gray-400">Size</span>
                        <span className="text-light">{selectedSize}</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-gray-400">Subtotal</span>
                      <span className="text-light">{formatIdrFromUsd(price)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Shipping</span>
                      <span className="text-light">
                        {(() => {
                          const c = (shipping && typeof shipping.cost === 'number') ? shipping.cost : 0;
                          const label = c === 0 ? 'Free' : formatIdrFromUsd(c);
                          return `${label}${shipping?.option ? ` (${shipping.option.carrier})` : ''}`;
                        })()}
                      </span>
                    </div>
                  </div>
                  
                  <div className="flex justify-between font-bold text-lg pt-2 border-t border-gray-800 mt-2">
                    <span className="text-light">Total</span>
                    <span className="text-primary">{formatIdrFromUsd(calculateTotal())}</span>
                  </div>
                  
                  <div className="mt-6 pt-4 border-t border-gray-800">
                    <h3 className="font-bold text-light mb-2">Shipping Address</h3>
                    <p className="text-gray-400 text-sm">{shippingAddress.fullName}</p>
                    <p className="text-gray-400 text-sm">{shippingAddress.addressLine1}</p>
                    {shippingAddress.addressLine2 && (
                      <p className="text-gray-400 text-sm">{shippingAddress.addressLine2}</p>
                    )}
                    <p className="text-gray-400 text-sm">
                      {shippingAddress.city}, {shippingAddress.state} {shippingAddress.postalCode}
                    </p>
                    <p className="text-gray-400 text-sm">{shippingAddress.country}</p>
                  </div>
                  
                  <div className="mt-4 pt-4 border-t border-gray-800">
                    <h3 className="font-bold text-light mb-2">Shipping Method</h3>
                    <p className="text-gray-400 text-sm">
                      {shipping?.option?.carrier} - {shipping?.option?.service}
                    </p>
                    <p className="text-gray-400 text-sm">
                      Estimated delivery: {shipping?.option?.estimatedDelivery}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Payment;
